import React, { useState } from 'react';
import { FaMapPin } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// InputField component moved outside to prevent re-creation on every render
const InputField = React.memo(({ 
  label, 
  name, 
  placeholder, 
  required = false, 
  type = 'text',
  options = null,
  error = '',
  value,
  onChange
}) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {options ? (
      <select
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      />
    )}
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
));

const CompanyProfile = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  
  // Try to load data from localStorage first
  const loadSavedData = () => {
    try {
      const savedData = localStorage.getItem('onboardingData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData.companyProfile) {
          console.log('Loaded company profile from localStorage:', parsedData.companyProfile);
          return parsedData.companyProfile;
        }
      }
    } catch (error) {
      console.error('Error loading company profile from localStorage:', error);
    }
    return initialData;
  };

  const [formData, setFormData] = useState({
    legalName: loadSavedData().legalName || '',
    brandName: loadSavedData().brandName || '',
    naceCode: loadSavedData().naceCode || '',
    companySize: loadSavedData().companySize || '',
    website: loadSavedData().website || '',
    fiscalYearLabel: loadSavedData().fiscalYearLabel || '',
    timezone: loadSavedData().timezone || '',
    country: loadSavedData().country || '',
    city: loadSavedData().city || '',
    street: loadSavedData().street || ''
  });

  const [errors, setErrors] = useState({});

  const companySizeOptions = [
    t('workflow.onboarding.companyProfile.companySizeOptions.micro'),
    t('workflow.onboarding.companyProfile.companySizeOptions.small'),
    t('workflow.onboarding.companyProfile.companySizeOptions.medium'),
    t('workflow.onboarding.companyProfile.companySizeOptions.large')
  ];

  const timezoneOptions = [
    'UTC+00:00 (London)',
    'UTC+01:00 (Berlin, Paris)',
    'UTC+02:00 (Warsaw, Kiev)',
    'UTC-05:00 (New York)',
    'UTC-08:00 (Los Angeles)'
  ];

  const handleInputChange = React.useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[field]) {
        return {
          ...prev,
          [field]: ''
        };
      }
      return prev;
    });
  }, []);

  // Navigation handled by WorkflowTabView; submit/save can be triggered externally via onSubmit



  return (
    <div className="w-full">


      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <form className="space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label={t('workflow.onboarding.companyProfile.form.legalName')}
              name="legalName"
              placeholder={t('workflow.onboarding.companyProfile.form.legalNamePlaceholder')}
              required={true}
              error={errors.legalName}
              value={formData.legalName}
              onChange={handleInputChange}
            />
            
            <InputField
              label={t('workflow.onboarding.companyProfile.form.brandName')}
              name="brandName"
              placeholder={t('workflow.onboarding.companyProfile.form.brandNamePlaceholder')}
              value={formData.brandName}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label={t('workflow.onboarding.companyProfile.form.naceCode')}
              name="naceCode"
              placeholder={t('workflow.onboarding.companyProfile.form.naceCodePlaceholder')}
              required={true}
              error={errors.naceCode}
              value={formData.naceCode}
              onChange={handleInputChange}
            />
            
            <InputField
              label={t('workflow.onboarding.companyProfile.form.companySize')}
              name="companySize"
              placeholder={t('workflow.onboarding.companyProfile.form.companySizePlaceholder')}
              required={true}
              options={companySizeOptions}
              value={formData.companySize}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label={t('workflow.onboarding.companyProfile.form.website')}
              name="website"
              placeholder={t('workflow.onboarding.companyProfile.form.websitePlaceholder')}
              type="url"
              value={formData.website}
              onChange={handleInputChange}
              />
            
            <InputField
              label={t('workflow.onboarding.companyProfile.form.fiscalYearLabel')}
              name="fiscalYearLabel"
              placeholder={t('workflow.onboarding.companyProfile.form.fiscalYearLabelPlaceholder')}
              value={formData.fiscalYearLabel}
              onChange={handleInputChange}
            />
          </div>

          <InputField
            label={t('workflow.onboarding.companyProfile.form.timezone')}
            name="timezone"
            placeholder={t('workflow.onboarding.companyProfile.form.timezonePlaceholder')}
            required={true}
            options={timezoneOptions}
            value={formData.timezone}
            onChange={handleInputChange}
          />

          {/* Headquarters Address Section */}
          <div className="border-t pt-4">
            <div className="flex items-center mb-4">
              <FaMapPin className="text-gray-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-800">
                Adres Siedziby
              </h2>
            </div>
            
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label={t('workflow.onboarding.companyProfile.form.country')}
                name="country"
                placeholder={t('workflow.onboarding.companyProfile.form.countryPlaceholder')}
                required={true}
                error={errors.country}
                value={formData.country}
                onChange={handleInputChange}
              />
              
              <InputField
                label={t('workflow.onboarding.companyProfile.form.city')}
                name="city"
                placeholder={t('workflow.onboarding.companyProfile.form.cityPlaceholder')}
                required={true}
                value={formData.city}
                onChange={handleInputChange}
              />
            </div>
            
            <InputField
              label={t('workflow.onboarding.companyProfile.form.street')}
              name="street"
              placeholder={t('workflow.onboarding.companyProfile.form.streetPlaceholder')}
              required={true}
              value={formData.street}
              onChange={handleInputChange}
            />
          </div>


        </form>
      </div>
    </div>
  );
};

export default CompanyProfile;
